/**
 * Competence Map — shared translation helpers.
 *
 * All keys are LITERAL strings (literal-i18n-keys rule): dynamic values
 * (state, CEFR band) go through exhaustive switches, never template
 * interpolation of the key itself.
 *
 * The competence NAME is not translated here — `translateErrorType` in
 * `components/practice/recommendedText.ts` already covers every taxonomy slug in
 * both locale files (61 explicit cases plus `other` via its default), and re-implementing it would be a second list to keep in sync
 * with the taxonomy. This screen therefore adds no per-competence keys.
 *
 * @see docs/specs/mobile/competence-map.mobile.md
 */

import type { TFunction } from 'i18next';
import type { CompetenceMapItem, CompetenceMapResponse } from '../../generated/api/http-contracts';

export type CompetenceState = CompetenceMapItem['state'];
export type CefrBand = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

/** Band order for sections — matches the server's inventory ordering. */
export const CEFR_BANDS: CefrBand[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/**
 * Contrastive difficulty at or above this marks a competence as "expected hard
 * for your Spanish". Same constant and same rule as the Sound Map's
 * `EXPECTED_HARD_MIN_DIFFICULTY` — the two maps must not disagree about what
 * counts as a warning.
 */
export const EXPECTED_HARD_MIN_DIFFICULTY = 4;

/**
 * Badge rule: the expected-hard hint shows only where there is no evidence yet.
 *
 * `upcoming` is included alongside `untried` deliberately — a hard C1 structure
 * a learner has not reached is exactly the thing worth flagging early, and the
 * badge says "expect this to be hard", not "you are failing this".
 */
export function showsExpectedHardBadge(competence: CompetenceMapItem): boolean {
  return (
    (competence.state === 'untried' || competence.state === 'upcoming') &&
    competence.difficulty !== null &&
    competence.difficulty >= EXPECTED_HARD_MIN_DIFFICULTY
  );
}

/**
 * User-facing label for a mastery state (chips + detail sheet pill).
 *
 * The trailing return is not dead code: the switch is exhaustive over today's
 * union, but nothing validates the response at runtime, so a server that grows
 * a sixth state would otherwise render a blank pill on an old client. The N-1
 * contract promises graceful degradation.
 */
export function translateStateLabel(t: TFunction, state: CompetenceState): string {
  switch (state) {
    case 'mastered': return t('competenceMap.states.mastered');
    case 'learning': return t('competenceMap.states.learning');
    case 'struggling': return t('competenceMap.states.struggling');
    case 'untried': return t('competenceMap.states.untried');
    case 'upcoming': return t('competenceMap.states.upcoming');
  }
  return t('competenceMap.states.untried');
}

/** Section title for a CEFR band. */
export function translateBandTitle(t: TFunction, band: CefrBand): string {
  switch (band) {
    case 'A1': return t('competenceMap.bands.A1');
    case 'A2': return t('competenceMap.bands.A2');
    case 'B1': return t('competenceMap.bands.B1');
    case 'B2': return t('competenceMap.bands.B2');
    case 'C1': return t('competenceMap.bands.C1');
    case 'C2': return t('competenceMap.bands.C2');
  }
}

/** Spoken form of a chip for VoiceOver/TalkBack: "Past tense, en progreso". */
export function chipA11yLabel(t: TFunction, name: string, competence: CompetenceMapItem): string {
  const state = translateStateLabel(t, competence.state);
  return showsExpectedHardBadge(competence)
    ? t('competenceMap.chip.a11yWithHard', { name, state })
    : t('competenceMap.chip.a11y', { name, state });
}

/**
 * Plural-aware per-state counts joined as one line. `upcoming` is included so
 * the learner can see that the untouched bulk of the map is "later", not
 * "failed" — the whole point of splitting absence in two (CMAP-001).
 */
export function formatSummaryLine(
  t: TFunction,
  summary: CompetenceMapResponse['summary'],
): string {
  return [
    t('competenceMap.summary.mastered', { count: summary.mastered }),
    t('competenceMap.summary.learning', { count: summary.learning }),
    t('competenceMap.summary.struggling', { count: summary.struggling }),
    t('competenceMap.summary.untried', { count: summary.untried }),
    t('competenceMap.summary.upcoming', { count: summary.upcoming }),
  ].join(' · ');
}

/**
 * The progress denominator EXCLUDES `upcoming`.
 *
 * A progress ring must be completable — the same argument ADR-036 makes for
 * the *attainable* vocabulary core. For an A2 learner 28 of the 61 sit above
 * their level (B1 19 · B2 4 · C1 5); counting those measures the curriculum,
 * not the learner.
 */
export function attainableTotal(summary: CompetenceMapResponse['summary']): number {
  return summary.mastered + summary.learning + summary.struggling + summary.untried;
}
